import { useState, useEffect, useCallback, useRef } from 'react';
import { TTSService, getTTSService } from '../services/tts/ttsService';
import { TTSConfig, TTSState } from '../services/tts/types';

interface UseTTSOptions {
  autoWarmup?: boolean;
  config?: Partial<TTSConfig>;
}

interface UseTTSHook {
  ttsService: TTSService;
  state: TTSState;
  speak: (text: string) => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  updateConfig: (config: Partial<TTSConfig>) => void;
  isSupported: boolean;
}

// 动态检测 speechSynthesis 是否可用
function checkSupport(): boolean {
  if (typeof window === 'undefined') return false;
  // 直接检查
  if ('speechSynthesis' in window) return true;
  // 某些旧浏览器用 webkit 前缀
  if ('webkitSpeechSynthesis' in window) return true;
  return false;
}

export function useTTS(options: UseTTSOptions = {}): UseTTSHook {
  const { autoWarmup = true, config } = options;
  const [state, setState] = useState<TTSState>({
    isSpeaking: false,
    isPaused: false,
    currentChunkIndex: 0,
    totalChunks: 0,
    currentText: '',
    progress: 0,
  });

  // isSupported 改为 state，支持动态检测
  const [isSupported, setIsSupported] = useState(checkSupport);

  const ttsServiceRef = useRef<TTSService>(getTTSService(config));

  // 初始化 + 动态检测 speechSynthesis
  useEffect(() => {
    // 立即检测一次
    setIsSupported(checkSupport());

    // 监听 onvoiceschanged：某些浏览器在语音列表加载后才暴露 speechSynthesis
    if ('speechSynthesis' in window) {
      const handler = () => setIsSupported(true);
      window.speechSynthesis.onvoiceschanged = handler;
      // 如果 voices 已经有值，直接标记支持
      if (window.speechSynthesis.getVoices().length > 0) {
        setIsSupported(true);
      }
    }

    // 定时重试检测（兜底：某些浏览器 speechSynthesis 延迟可用）
    const retryTimer = setInterval(() => {
      if (checkSupport()) {
        setIsSupported(true);
        clearInterval(retryTimer);
      }
    }, 1000);

    // 5秒后停止重试
    const stopTimer = setTimeout(() => clearInterval(retryTimer), 5000);

    return () => {
      clearInterval(retryTimer);
      clearTimeout(stopTimer);
      try {
        if ('speechSynthesis' in window) {
          window.speechSynthesis.onvoiceschanged = null;
        }
      } catch (e) { /* 忽略 */ }
    };
  }, []);

  // TTS 事件监听
  useEffect(() => {
    if (!isSupported) return;

    const ttsService = ttsServiceRef.current;

    const handleStateChange = () => {
      setState(ttsService.getState());
    };

    ttsService.on('start', handleStateChange);
    ttsService.on('end', handleStateChange);
    ttsService.on('pause', handleStateChange);
    ttsService.on('resume', handleStateChange);

    // 预热语音引擎
    if (autoWarmup) {
      ttsService.warmup();
    }

    return () => {
      ttsService.stop();
      ttsService.off('start', handleStateChange);
      ttsService.off('end', handleStateChange);
      ttsService.off('pause', handleStateChange);
      ttsService.off('resume', handleStateChange);
    };
  }, [isSupported, autoWarmup]);

  // 开始朗读
  const speak = useCallback(async (text: string) => {
    if (!checkSupport()) {
      console.warn('[TTS] 浏览器不支持语音合成');
      return;
    }
    await ttsServiceRef.current.speak(text);
  }, []);

  const pause = useCallback(() => {
    ttsServiceRef.current.pause();
  }, []);

  const resume = useCallback(() => {
    ttsServiceRef.current.resume();
  }, []);

  const stop = useCallback(() => {
    ttsServiceRef.current.stop();
  }, []);

  const updateConfig = useCallback((newConfig: Partial<TTSConfig>) => {
    ttsServiceRef.current.updateConfig(newConfig);
  }, []);

  return {
    ttsService: ttsServiceRef.current,
    state,
    speak,
    pause,
    resume,
    stop,
    updateConfig,
    isSupported,
  };
}

export default useTTS;
